import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, CheckCircle2, AlertTriangle, Loader2, Send
} from 'lucide-react';
import { authorizedFetch } from '../utils/api';
import './ServiceProposals.css';

const GROUP_LABELS = {
  urgent: 'Urgent Security',
  upgrades: 'Upgrades',
  replacements: 'Replacements',
  recurring: 'Recurring Services',
  separate: 'Quoted Separately',
  other: 'Additional Items'
};
const GROUP_ORDER = ['urgent', 'replacements', 'upgrades', 'recurring', 'other', 'separate'];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Client-facing remediation proposals: review each line, check what to
// approve now. Unchecked items are kept on file — nothing is lost by
// approving only part of the proposal.
export default function ServiceProposals() {
  const navigate = useNavigate();
  const { planId } = useParams(); // deep link from the dashboard Proposals tab
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (planId) {
      openPlan(planId);
      return;
    }
    (async () => {
      try {
        const res = await authorizedFetch('/api/remediation/portal/plans');
        if (res.ok) setPlans(await res.json());
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const openPlan = async (id) => {
    setLoading(true);
    setDone(false);
    try {
      const res = await authorizedFetch(`/api/remediation/portal/plans/${id}`);
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
        // Pre-check items: everything approvable starts checked (opt-out UX)
        setChecked(new Set(
          data.items
            .filter(i => (i.item_type === 'labor' || i.item_type === 'parts_labor')
                          && i.client_state !== 'on_hold')
            .map(i => i.id)
        ));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const approvedTotal = plan
    ? plan.items
        .filter(i => checked.has(i.id))
        .reduce((sum, i) => sum + Number(i.line_total || 0), 0)
    : 0;

  const submit = async () => {
    if (!window.confirm(
      `Approve ${checked.size} item(s) for a total of ${money(approvedTotal)}?\n` +
      'Unchecked items will be kept on file — Macula can re-propose them anytime.')) return;
    setSubmitting(true);
    try {
      const res = await authorizedFetch(`/api/remediation/portal/plans/${plan.id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ approved_item_ids: [...checked] })
      });
      if (res.ok) {
        setDone(true);
        openPlan(plan.id);
      } else {
        const err = await res.json().catch(() => ({}));
        window.alert(err.detail || 'Submission failed — please try again');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="sp-page"><div className="sp-loading"><Loader2 className="sp-spin" size={24} /> Loading...</div></div>;
  }

  // ---- detail ----
  if (plan) {
    const responded = plan.status === 'responded';
    const groups = GROUP_ORDER.filter(g => plan.items.some(i => i.group_key === g));
    return (
      <div className="sp-page">
        <div className="sp-header">
          <button className="sp-back" onClick={() => {
            setPlan(null);
            if (planId) navigate('/'); // deep-linked from dashboard — go back there
          }}>
            <ArrowLeft size={16} /> {planId ? 'Dashboard' : 'All proposals'}
          </button>
          <h1>{plan.title}</h1>
        </div>

        {done && (
          <div className="sp-banner success">
            <CheckCircle2 size={16} /> Thank you — your selections were submitted.
            Macula Networks will be in touch to schedule the approved work.
          </div>
        )}
        {responded && !done && (
          <div className="sp-banner info">
            This proposal was answered{plan.responded_at ? ` on ${new Date(plan.responded_at).toLocaleDateString()}` : ''}.
            Items marked on hold are kept on file.
          </div>
        )}

        {groups.map(g => (
          <div key={g} className="sp-group">
            <h2>{GROUP_LABELS[g]}</h2>
            <div className="sp-items">
              {plan.items.filter(i => i.group_key === g).map(item => {
                const priceable = item.item_type === 'labor' || item.item_type === 'parts_labor';
                return (
                  <label key={item.id}
                    className={`sp-item ${!priceable ? 'sp-item-static' : ''} ${checked.has(item.id) && !responded ? 'sp-item-checked' : ''}`}>
                    {priceable && !responded && (
                      <input type="checkbox" checked={checked.has(item.id)} onChange={() => toggle(item.id)} />
                    )}
                    {responded && priceable && (
                      <span className={`sp-state ${item.client_state}`}>
                        {item.client_state === 'approved' ? 'Approved' : 'On hold'}
                      </span>
                    )}
                    <div className="sp-item-body">
                      <div className="sp-item-title">
                        {item.priority && <AlertTriangle size={13} className="sp-prio" />}
                        {item.machine_name && item.machine_name !== '(fleet)' && (
                          <span className="sp-machine">{item.machine_name}</span>
                        )}
                        <span>{item.action_text}</span>
                      </div>
                      {item.parts_description && (
                        <div className="sp-item-sub">Hardware: {item.parts_description}</div>
                      )}
                    </div>
                    <div className="sp-item-price">
                      {item.item_type === 'quote_separately' ? <em>Will be quoted separately</em>
                        : item.item_type === 'no_charge' ? <em>No charge</em>
                        : money(item.line_total)}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {!responded && (
          <div className="sp-footer">
            <div className="sp-total">
              Approving <b>{checked.size}</b> item(s) — total <b>{money(approvedTotal)}</b>
            </div>
            <button className="sp-submit" onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 size={15} className="sp-spin" /> : <Send size={15} />}
              Submit approvals
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---- list ----
  return (
    <div className="sp-page">
      <div className="sp-header">
        <h1><ClipboardList size={20} /> Service Proposals</h1>
      </div>
      {plans.length === 0 ? (
        <div className="sp-empty">No proposals at the moment.</div>
      ) : (
        <div className="sp-list">
          {plans.map(p => (
            <button key={p.id} className="sp-list-item" onClick={() => openPlan(p.id)}>
              <span className="sp-list-title">{p.title}</span>
              <span className={`sp-status ${p.status}`}>
                {p.status === 'sent' ? 'Awaiting your review' : 'Responded'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
